import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from 'react';
import {Card, CardBody, CardTitle, Col, Form, FormGroup, Input, Label, Row} from 'reactstrap';

import {error, info, infoMsg, REQUIRED, saveMsg, selectOption, success, updateMsg} from "../../../components/i18n/i18n";
import {showToastCustom} from "../../../components/toast/customToast";
import {ButtonCancel, ButtonEdit, ButtonSubmit, GenericTableButton} from "../../../components/buttons/buttonsActions";
import * as Yup from "yup";
import {useForm} from "react-hook-form";
import {showLoader} from "../../../components/loader/loader";
import Switch from "react-bootstrap-switch";
import {scheduleService} from "../../../jwt/_services/clasificator/scheduleService";
import Select from "react-select";
import {typeSeasonProvider} from "../../../providers/typeSeasonProvider";
import {activityService} from "../../../jwt/_services/core/activityService";
import {TT_HISTORY} from "../../../components/i18n/toolTips";
import {LogHistoryModal} from "../../../components/logHistory/logHistoryModal";

export const ScheduleForm = forwardRef((props, ref) => {

    const [editMode, setEditMode] = useState(!!props?.item?.id);
    const [disableInputs, setDisableInputs] = useState(false);
    const [, setSaveSuccess] = useState(false);
    const [savedData, setSavedData] = useState(editMode ? props.item : null);
    const [loading, setLoading] = useState(false);

    const [viewMode, setViewMode] = useState(props?.viewMode);
    const [showEditButton, setShowEditButton] = useState(props?.showEditButton);
    const [showSaveButton, setShowSaveButton] = useState(props?.viewMode === undefined);
    const [tdtProps] = useState(!!props?.tdtProps);

    const [status, setStatus] = useState(editMode ? props.item?.status : false);
    const [disableTypeSeason, setDisableTypeSeason] = useState(editMode);

    const [activity, setActivity] = useState(props.activity);
    const [typeSeason, setTypeSeason] = useState(editMode ? props.item?.typeSeasonId : undefined);
    const [time12, setTime12] = useState(editMode ? props.item?.time12 : undefined);
    const [time24, setTime24] = useState(editMode ? props.item?.time24 : undefined);
    const [timeFrom, setTimeFrom] = useState('00:00');
    const [timeTo, setTimeTo] = useState('00:00');

    const [duration, setDuration] = useState(editMode ? props.item?.duration : '');
    const [typeSeasonOptions, setTypeSeasonOptions] = useState([]);

    const [showModal, setShowModal] = useState(true)
    const modalRef = useRef(null);

    const showHistory = () => {
        setShowModal(true);
        modalRef.current.showModal(savedData);
    }

    useEffect(() => {
        if (props.item?.time24 !== null) {
            separateFromTo(props.item?.time24);
            if (!editMode) {
                setSchedule(timeFrom, timeTo)
            }
        }

        if (viewMode !== undefined) {
            setDisableInputs(true);

            if (savedData?.activity) {
                setLoading(true);
                activityService.getItem(savedData?.activity?.id)
                    .then(data => {
                        setActivity(data);
                        setLoading(false);
                    })
                    .catch(_ => {
                        setLoading(false);
                    });
            }

            if (savedData?.typeSeason) {
                setLoading(true);
                typeSeasonProvider.getItem(savedData?.typeSeason.id)
                    .then(data => {
                        setTypeSeason(data);
                        setLoading(false);
                    })
                    .catch(_ => {
                        setLoading(false);
                    });
            }
        }

        setLoading(true);
        typeSeasonProvider.getOptions()
            .then(result => {
                setTypeSeasonOptions(result);
                if (props.item?.typeSeasonId) {
                    typeSeasonProvider.getItem(typeSeason)
                        .then(data => {
                            setLoading(false);
                            setTypeSeason(data);
                        });
                } else {
                    setLoading(false);
                }
            })
            .catch(_ => {
                setLoading(false);
            })

    }, []);

    const validationSchema = Yup.object().shape({
        time24: Yup.string().required(REQUIRED)
    });

    const {register, handleSubmit, reset, errors} = useForm({
        validationSchema: validationSchema,
        defaultValues: {
            duration: savedData?.duration,
            time12: savedData?.time12,
            time24: savedData?.time24,
            status: savedData?.status,
            season: savedData?.season,
        }
    });

    const onSubmit = (data, _) => {
        if (typeSeason == null)
            return showToastCustom(error, selectOption);

        setLoading(true);
        let dataRequest = {
            duration: data.duration,
            time12: data.time12,
            time24: data.time24,
            // Booleans
            status: status,
            // Objects
            typeSeason: typeSeason,
            activity: activity,
        };

        if (!editMode) {
            //Save
            return scheduleService.saveItem(dataRequest)
                .then(_ => {
                    showToastCustom(success, saveMsg);
                    resetForm();
                })
                .catch(errorMessage => {
                    setLoading(false);
                    showToastCustom(error, errorMessage);
                });
        } else {
            //Update
            let id = savedData.id;
            delete savedData.id;

            if (JSON.stringify(savedData) === JSON.stringify(dataRequest))
                return showToastCustom(info, infoMsg);

            dataRequest = { ...dataRequest, id }

            return scheduleService.updateItem(id, dataRequest)
                .then(_ => {
                    if (viewMode !== undefined) {
                        props.setStatusAfterEdit();
                    }
                    showToastCustom(success, updateMsg);
                    resetForm();
                })
                .catch(errorMessage => {
                    setLoading(false);
                    showToastCustom(error, errorMessage);
                });
        }
    };

    const resetForm = () => {
        setTime12(undefined);
        setTime24(undefined);
        setDuration('');
        setStatus(false);
        setLoading(false);
        setTypeSeason(null);
        setSavedData(null);
        setEditMode(false);
        setDisableInputs(false);
        setDisableTypeSeason(false);
        setSaveSuccess(false);
        reset();

        if (viewMode === undefined)
            props.refreshList();
    }

    const separateFromTo = (time) => {
        if (time !== undefined) {
            setTimeFrom(time.split('-')[0].trim());
            setTimeTo(time.split('-')[1]?.trim() !== undefined ? time.split('-')[1]?.trim() : '00:00');
        }
    }

    const setSchedule = (from, to) => {
        const fromh24 = from.split(':')[0];
        const fromm24 = from.split(':')[1];

        let toh24 = to.split(':')[0];
        let tom24 = to.split(':')[1];

        if (fromh24 > toh24 || (fromh24 === toh24 && fromm24 > tom24)) {
            to = from;

            toh24 = to.split(':')[0];
            tom24 = to.split(':')[1];
            setTimeTo(to);
        }

        let from12;
        let to12;

        if (fromh24 >= 12) {
            from12 = fromh24 === "12" ? `12:${fromm24} PM` : `${fromh24 - 12}:${fromm24} PM`;
        } else {
            from12 = `${fromh24}:${fromm24} AM`;
        }

        if (toh24 >= 12) {
            to12 = toh24 === "12" ? `12:${tom24} PM` : `${toh24 - 12}:${tom24} PM`;
        } else {
            to12 = `${toh24}:${tom24} AM`;
        }

        if (from === to){
            setTime24(`${from}`);
            setTime12(`${from12}`);
        } else {
            setTime24(`${from} - ${to}`);
            setTime12(`${from12} - ${to12}`);
        }
    }

    const onTimeFromChange = (value) => {
        setTimeFrom(value);
        setSchedule(value, timeTo);
    }

    const onTimeToChange = (value) => {
        setTimeTo(value);
        setSchedule(timeFrom, value);
    }

    // ----------------------------------------------
    //            CHANGE HANDLERS
    // ----------------------------------------------
    const statusChange = () => {
        setStatus(!status);
    }

    const onDurationChange = (e) => {
        setDuration(e.target.value);
    }

    const onSeasonChange = (value) => {
        setTypeSeason(value);
    }

    useImperativeHandle(ref, () => ({
        setFormEdit(itemP) {
            beginEdit(itemP);
        }
    }));

    const beginEdit = (itemP) => {
        if (viewMode === undefined && itemP) {
            setStatus(itemP?.status);
            setSavedData(itemP);
            setEditMode(true);
            setDisableTypeSeason(true);
            setTime12(itemP.time12);
            setTime24(itemP.time24);
            setDuration(itemP.duration);

            if (itemP.time24 !== null) {
                separateFromTo(itemP.time24);
            }

            if (itemP.typeSeasonId) {
                setTypeSeason(typeSeasonOptions.find(ts => ts.id === itemP?.typeSeasonId))
            }

            if (viewMode) {
                setViewMode(false);
                setShowEditButton(false);
                setShowSaveButton(true);
                props.setEdit();
            }
        } else if (viewMode) {
            setStatus(savedData?.status);
            setSavedData(savedData);
            setEditMode(true);
            setDisableTypeSeason(true);
            setDisableInputs(false);
            setTime12(savedData.time12);
            setTime24(savedData.time24);
            setDuration(savedData.duration);

            if (itemP.time24 !== null) {
                separateFromTo(itemP.time24);
            }

            setViewMode(false);
            setShowEditButton(false);
            setShowSaveButton(true);
            props.setEdit();
        } else {
            resetForm();
        }
    }

    const getClassName = (fieldName) => {
        if (tdtProps === undefined)
            return '';

        if (tdtProps?.changedFields?.toString().includes(fieldName)) {
            switch (tdtProps?.action) {
                case 'CREATE' :
                    return 'tdt_viewer_lbl_new';
                case 'UPDATE' :
                    return 'tdt_viewer_lbl_edited';
                case 'DELETE' :
                    return 'tdt_viewer_lbl_deleted';
                default :
                    return '';
            }
        } else
            return '';
    }

    return (
        <Card>
            {loading && showLoader()}
            <CardTitle className="bg-light border-bottom p-3 mb-0">
                <i className="fas fa-clock mr-2"/>
                 Horarios de actividad <Label className="label-type-season-today">{activity?.number + ' - ' + activity?.name} </Label>
                <div className="float-right">
                    {
                        showEditButton ? <ButtonEdit onClick={beginEdit}/> : <div/>
                    }

                    {
                        editMode && <div className="float-right">
                            <GenericTableButton
                                toolTip={TT_HISTORY}
                                icon={'fa-clock'}
                                permission={'search'}
                                onClick={showHistory}
                            />
                        </div>
                    }
                </div>
            </CardTitle>
            <CardBody>
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label>ID</Label>
                                <Input type="id" name="id"
                                       disabled
                                       value={editMode ? savedData?.id : ''}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label>Firebase ID</Label>
                                <Input type="uid" name="uid"
                                       disabled
                                       value={editMode ? savedData?.uid : ''}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="3">
                            <FormGroup>
                                <Label className={getClassName(('timeFrom'))}>Desde</Label>
                                <Input type="time" name="timeFrom"
                                       placeholder="Ingrese hora"
                                       innerRef={register}
                                       value={timeFrom}
                                       disabled={disableInputs}
                                       onChange={e => onTimeFromChange(e.target.value)}
                                       className={(errors.timeFrom ? ' is-invalid' : '')}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="3">
                            <FormGroup>
                                <Label className={getClassName(('timeTo'))}>Hasta</Label>
                                <Input type="time" name="timeTo"
                                       placeholder="Ingrese hora"
                                       innerRef={register}
                                       value={timeTo}
                                       disabled={disableInputs}
                                       onChange={e => onTimeToChange(e.target.value)}
                                       className={(errors.timeTo ? ' is-invalid' : '')}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="3">
                            <FormGroup>
                                <Label>Horario 24 hr</Label>
                                <Input type="text" name="time24"
                                       disabled
                                       value={time24}
                                       innerRef={register}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="3">
                            <FormGroup>
                                <Label>Horario 12 hr</Label>
                                <Input type="text" name="time12"
                                       disabled
                                       value={time12}
                                       innerRef={register}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="4">
                            <FormGroup>
                                <Label>Tipo de temporada</Label>
                                <br/>
                                <Select
                                    value={typeSeason}
                                    options={typeSeasonOptions}
                                    onChange={onSeasonChange}
                                    placeholder="Selecciona una opción"
                                    isDisabled={disableTypeSeason}
                                    innerRef={register}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label>Duración</Label>
                                <Input type="text" name="duration"
                                       placeholder="Ingrese duración"
                                       value={duration}
                                       onChange={onDurationChange}
                                       innerRef={register}
                                       disabled={disableInputs}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label>Activo</Label>
                                <br/>
                                <Switch animate={true} offText={"No"} onText={"Si"}
                                        value={status}
                                        disabled={disableInputs}
                                        innerRef={register}
                                        onChange={statusChange}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <CardBody className="border-top">
                        <div className="float-right">
                            {showSaveButton ? <ButtonSubmit/> : <div/>}
                            {viewMode === undefined ? <ButtonCancel onClick={props.setForm}/> : <div/>}
                        </div>
                    </CardBody>
                </Form>
            </CardBody>

            {
                showModal && <LogHistoryModal
                    ref={ modalRef }
                    item={ savedData }
                />
            }
        </Card>
    )
})